import { Grid } from '@mui/material';
import { parseHMtoSeconds, parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { TimeWindow } from '@nx-smartmonkey/shared/interfaces';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import { debounce } from 'lodash';
import { ChangeEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

const StyledInput = styled.input`
  &:focus-visible {
    outline: 1px solid ${RoutalPalette.primary40};
    border: 1px solid ${RoutalPalette.primary40};
  }
`;

interface TimeWindowInputProps {
  value?: number;
  containerStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  onChange: (value: number) => void;
}

const TimeWindowInput = ({
  value: initialValue,
  containerStyle = {},
  inputStyle = {},
  onChange,
}: TimeWindowInputProps) => {
  const [value, setValue] = useState<number | undefined>(initialValue);

  useEffect(() => {
    value !== initialValue && setValue(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = parseHMtoSeconds(event.target.value);
    setValue(newValue);
    onChange(newValue);
  };

  return (
    <Grid container flexDirection="row" style={containerStyle}>
      <StyledInput
        type="time"
        value={parseSecondsToHM(value)}
        style={{
          border: `1px solid ${RoutalPalette.secondary00}`,
          borderRadius: `5px`,
          textAlign: `center`,
          height: `27px`,
          width: `100%`,
          padding: `10px 8px`,
          fontFamily: `Geist Variable`,
          fontSize: `15px`,
          color: RoutalPalette.primary80,
          ...inputStyle,
        }}
        onChange={handleChange}
      />
    </Grid>
  );
};

const MINUTE = 60;

type RoutalTimeWindowInputProps = {
  timeWindow?: TimeWindow;
  onTimewindowChange?: (timeWindow: TimeWindow) => void;
  timeWindowInputStyle?: React.CSSProperties;
  timeWindowInputContainerStyle?: React.CSSProperties;
};

export const RoutalTimeWindowInput = ({
  timeWindow: initialTimeWindow,
  onTimewindowChange,
  timeWindowInputStyle,
  timeWindowInputContainerStyle,
}: RoutalTimeWindowInputProps) => {
  const [timeWindow, setTimeWindow] = useState<TimeWindow | undefined>(initialTimeWindow);

  const checkOverlapping = (value: TimeWindow): boolean => {
    return value[0] > value[1];
  };

  const handleUpdateTimeWindow = debounce((value: number, sideChanged: `start` | `end`) => {
    const newTimeWindow: TimeWindow =
      sideChanged === `start` ? [value, timeWindow?.[1] ?? value + MINUTE] : [timeWindow?.[0] ?? value - MINUTE, value];

    if (checkOverlapping(newTimeWindow)) {
      if (sideChanged === `start`) {
        newTimeWindow[1] = newTimeWindow[0] + MINUTE;
      } else {
        newTimeWindow[0] = newTimeWindow[1] - MINUTE;
      }
    }
    setTimeWindow(newTimeWindow);
    onTimewindowChange?.(newTimeWindow);
  }, 500);

  return (
    <Grid container flexDirection="row" flexWrap="nowrap" alignItems="center">
      <TimeWindowInput
        value={timeWindow?.[0]}
        onChange={(value) => handleUpdateTimeWindow(value, `start`)}
        containerStyle={timeWindowInputContainerStyle}
        inputStyle={timeWindowInputStyle}
      />
      <div style={{ width: `16px`, margin: `4px`, borderBottom: `1px solid ${RoutalPalette.primary40}` }} />
      <TimeWindowInput
        value={timeWindow?.[1]}
        onChange={(value) => handleUpdateTimeWindow(value, `end`)}
        containerStyle={timeWindowInputContainerStyle}
        inputStyle={timeWindowInputStyle}
      />
    </Grid>
  );
};
